'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import StatusBadge from '@/components/ui/StatusBadge';
import { toast } from 'sonner';
import BreadcrumbMap from './BreadcrumbMap';
import StatusTimeline from './StatusTimeline';

// ─── Types ────────────────────────────────────────────────────────────────────

type DeliveryStatus =
  | 'matched' |'payment_escrowed' |'pickup_confirmed' |'in_transit' |'delivered' |'completed';

interface Delivery {
  id: string;
  route: string;
  pickupCity: string;
  destinationCity: string;
  parcelDescription: string;
  parcelCategory: string;
  status: DeliveryStatus;
  travelerName: string;
  travelerId: string;
  senderId: string;
  transportMethod: string;
  departureTime: string;
  estimatedArrival: string;
  offeredPrice: number;
  routeProgress: number;
  currentLocation: string;
  etaMinutes: number | null;
  etaUpdatedAt: string | null;
  matchedAt: string | null;
  paymentEscrowedAt: string | null;
  pickupConfirmedAt: string | null;
  inTransitAt: string | null;
  deliveredAt: string | null;
  completedAt: string | null;
  lastUpdate: string;
}

interface TrackingEvent {
  id: string;
  event_type: string;
  title: string;
  description: string;
  location_name: string;
  latitude: number | null;
  longitude: number | null;
  created_at: string;
}

interface Breadcrumb {
  id: string;
  latitude: number;
  longitude: number;
  location_name: string;
  speed_kmh: number | null;
  created_at: string;
}

const statusConfig: Record<DeliveryStatus, { label: string; variant: 'info' | 'warning' | 'primary' | 'success' | 'muted' | 'danger' }> = {
  matched:           { label: 'Matched',          variant: 'info' },
  payment_escrowed:  { label: 'Payment Escrowed', variant: 'warning' },
  pickup_confirmed:  { label: 'Pickup Confirmed', variant: 'primary' },
  in_transit:        { label: 'In Transit',       variant: 'info' },
  delivered:         { label: 'Delivered',        variant: 'success' },
  completed:         { label: 'Completed',        variant: 'success' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-KE', {
    day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function fmtMinuteTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-KE', {
    day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
  });
}

function etaLabel(minutes: number | null): string {
  if (minutes === null) return '—';
  if (minutes <= 0) return 'Arriving now';
  if (minutes < 60) return `~${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `~${h}h ${m}m` : `~${h}h`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function LiveTrackingContent() {
  const { user } = useAuth();
  const supabase = createClient();

  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [selected, setSelected] = useState<Delivery | null>(null);
  const [events, setEvents] = useState<TrackingEvent[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'timeline' | 'breadcrumbs'>('timeline');
  const [etaInput, setEtaInput] = useState('');
  const [updatingEta, setUpdatingEta] = useState(false);
  const [locationInput, setLocationInput] = useState('');
  const [updatingLocation, setUpdatingLocation] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('active');
  const pulseRef = useRef<NodeJS.Timeout | null>(null);
  const [pulse, setPulse] = useState(false);

  // ── Fetch deliveries ────────────────────────────────────────────────────────
  const fetchDeliveries = useCallback(async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from('deliveries')
      .select('*')
      .or(`sender_id.eq.${user.id},traveler_id.eq.${user.id}`)
      .order('created_at', { ascending: false });

    if (error) { console.error(error); return; }

    const mapped: Delivery[] = (data || []).map(d => ({
      id: d.id,
      route: d.route || `${d.pickup_city} → ${d.destination_city}`,
      pickupCity: d.pickup_city,
      destinationCity: d.destination_city,
      parcelDescription: d.parcel_description,
      parcelCategory: d.parcel_category,
      status: d.status as DeliveryStatus,
      travelerName: d.traveler_name || 'Traveler',
      travelerId: d.traveler_id || '',
      senderId: d.sender_id || '',
      transportMethod: d.transport_method || '—',
      departureTime: d.departure_time || '',
      estimatedArrival: d.estimated_arrival || '',
      offeredPrice: parseFloat(d.offered_price_kes) || 0,
      routeProgress: d.route_progress || 0,
      currentLocation: d.current_location || '',
      etaMinutes: d.eta_minutes ?? null,
      etaUpdatedAt: d.eta_updated_at || null,
      matchedAt: d.matched_at || d.created_at || null,
      paymentEscrowedAt: d.payment_escrowed_at || null,
      pickupConfirmedAt: d.pickup_confirmed_at || null,
      inTransitAt: d.in_transit_at || null,
      deliveredAt: d.delivered_at || null,
      completedAt: d.completed_at || null,
      lastUpdate: d.last_update || d.updated_at || '',
    }));

    setDeliveries(mapped);
    setLoading(false);

    // Keep selected in sync
    setSelected(prev => {
      if (!prev) return mapped[0] ?? null;
      return mapped.find(d => d.id === prev.id) ?? prev;
    });
  }, [user]);

  // ── Fetch tracking events ───────────────────────────────────────────────────
  const fetchEvents = useCallback(async (deliveryId: string) => {
    const { data } = await supabase
      .from('tracking_events')
      .select('*')
      .eq('delivery_id', deliveryId)
      .order('created_at', { ascending: true });
    setEvents(data || []);
  }, []);

  // ── Fetch breadcrumbs ───────────────────────────────────────────────────────
  const fetchBreadcrumbs = useCallback(async (deliveryId: string) => {
    const { data } = await supabase
      .from('delivery_breadcrumbs')
      .select('*')
      .eq('delivery_id', deliveryId)
      .order('created_at', { ascending: true });
    setBreadcrumbs(data || []);
  }, []);

  // ── Initial load + realtime ─────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    fetchDeliveries();

    const channel = supabase
      .channel('live-tracking-main')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'deliveries' }, () => {
        fetchDeliveries();
        // Pulse indicator
        setPulse(true);
        if (pulseRef.current) clearTimeout(pulseRef.current);
        pulseRef.current = setTimeout(() => setPulse(false), 2000);
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tracking_events' }, (payload: any) => {
        if (payload.new?.delivery_id) fetchEvents(payload.new.delivery_id);
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'delivery_breadcrumbs' }, (payload: any) => {
        if (payload.new?.delivery_id) fetchBreadcrumbs(payload.new.delivery_id);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user, fetchDeliveries]);

  useEffect(() => {
    if (selected) {
      fetchEvents(selected.id);
      fetchBreadcrumbs(selected.id);
    }
  }, [selected?.id]);

  // ── Derived ─────────────────────────────────────────────────────────────────
  const isTraveler = selected && user?.id === selected.travelerId;

  const filtered = filterStatus === 'all'
    ? deliveries
    : filterStatus === 'active'
      ? deliveries.filter(d => !['completed', 'delivered'].includes(d.status))
      : deliveries.filter(d => d.status === filterStatus);

  // ── Traveler actions ────────────────────────────────────────────────────────
  const handleUpdateEta = async () => {
    if (!selected || !isTraveler) return;
    const mins = parseInt(etaInput, 10);
    if (isNaN(mins) || mins < 0) { toast.error('Enter a valid number of minutes'); return; }
    setUpdatingEta(true);
    try {
      await supabase.from('deliveries').update({
        eta_minutes: mins,
        eta_updated_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }).eq('id', selected.id);

      await supabase.from('tracking_events').insert({
        delivery_id: selected.id,
        event_type: 'eta_update',
        title: 'ETA Updated',
        description: `Traveler updated ETA to ${etaLabel(mins)}.`,
        location_name: selected.currentLocation || selected.pickupCity,
        recorded_by: user?.id,
      });

      toast.success('ETA updated');
      setEtaInput('');
      fetchDeliveries();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update ETA');
    } finally {
      setUpdatingEta(false);
    }
  };

  const handleUpdateLocation = async () => {
    if (!selected || !isTraveler) return;
    if (!locationInput.trim()) { toast.error('Enter a location name'); return; }
    setUpdatingLocation(true);
    try {
      await supabase.from('deliveries').update({
        current_location: locationInput.trim(),
        last_update: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }).eq('id', selected.id);

      await supabase.from('tracking_events').insert({
        delivery_id: selected.id,
        event_type: 'checkpoint',
        title: 'Location Checkpoint',
        description: `Traveler checked in at ${locationInput.trim()}.`,
        location_name: locationInput.trim(),
        recorded_by: user?.id,
      });

      // Also add breadcrumb (without GPS coords — text-based)
      await supabase.from('delivery_breadcrumbs').insert({
        delivery_id: selected.id,
        recorded_by: user?.id,
        location_name: locationInput.trim(),
      });

      toast.success('Location checkpoint recorded');
      setLocationInput('');
      fetchDeliveries();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update location');
    } finally {
      setUpdatingLocation(false);
    }
  };

  const handleShareGPS = async () => {
    if (!selected || !isTraveler) return;
    if (!navigator.geolocation) { toast.error('Geolocation not supported on this device'); return; }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude, speed, accuracy, heading } = pos.coords;
        try {
          await supabase.from('delivery_breadcrumbs').insert({
            delivery_id: selected.id,
            recorded_by: user?.id,
            latitude,
            longitude,
            speed_kmh: speed ? speed * 3.6 : null,
            accuracy_m: accuracy,
            heading_deg: heading,
            location_name: selected.currentLocation || 'GPS point',
          });

          await supabase.from('tracking_events').insert({
            delivery_id: selected.id,
            event_type: 'gps_ping',
            title: 'GPS Location Shared',
            description: `Precise location recorded (${latitude.toFixed(5)}, ${longitude.toFixed(5)}).`,
            location_name: selected.currentLocation || 'GPS point',
            latitude,
            longitude,
            recorded_by: user?.id,
          });

          toast.success('GPS location shared');
          fetchBreadcrumbs(selected.id);
        } catch (err: any) {
          toast.error(err?.message || 'Failed to share GPS');
        }
      },
      () => toast.error('Could not get GPS location. Check browser permissions.')
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading deliveries…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-800 text-foreground tracking-tight">Live Delivery Tracking</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Real-time status, ETA, geolocation breadcrumbs &amp; minute-precision timeline
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`flex items-center gap-1.5 text-xs font-600 px-3 py-1.5 rounded-full border transition-colors ${pulse ? 'bg-success/10 border-success/30 text-success' : 'bg-muted border-border text-muted-foreground'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${pulse ? 'bg-success animate-ping' : 'bg-muted-foreground'}`} />
            {pulse ? 'Update received' : 'Realtime active'}
          </span>
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'active', label: 'Active' },
          { key: 'in_transit', label: 'In Transit' },
          { key: 'delivered', label: 'Delivered' },
          { key: 'completed', label: 'Completed' },
          { key: 'all', label: 'All' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilterStatus(f.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-600 border transition-all ${
              filterStatus === f.key
                ? 'bg-primary text-white border-primary' :'bg-card text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'
            }`}
          >
            {f.label}
          </button>
        ))}
        <span className="ml-auto text-xs text-muted-foreground self-center">{filtered.length} deliveries</span>
      </div>

      {filtered.length === 0 ? (
        <div className="card-elevated p-12 text-center">
          <Icon name="TruckIcon" size={40} className="text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm font-600 text-foreground">No deliveries found</p>
          <p className="text-xs text-muted-foreground mt-1">
            {filterStatus === 'active' ? 'You have no active deliveries right now.' : 'Try a different filter.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* ── Left: delivery list ── */}
          <div className="lg:col-span-1 space-y-3">
            {filtered.map(d => {
              const cfg = statusConfig[d.status];
              const isSelected = selected?.id === d.id;
              return (
                <button
                  key={d.id}
                  onClick={() => setSelected(d)}
                  className={`w-full text-left rounded-xl border p-4 transition-all ${
                    isSelected
                      ? 'border-primary bg-primary/5 shadow-sm'
                      : 'border-border bg-card hover:border-primary/30 hover:bg-muted/30'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-700 text-foreground truncate">{d.route}</p>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{d.parcelCategory} · {d.parcelDescription}</p>
                    </div>
                    <StatusBadge variant={cfg.variant} label={cfg.label} size="sm" />
                  </div>

                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Icon name="UserIcon" size={11} />
                      {d.travelerName}
                    </span>
                    {d.etaMinutes !== null && d.status === 'in_transit' && (
                      <span className="flex items-center gap-1 text-primary font-600">
                        <Icon name="ClockIcon" size={11} />
                        {etaLabel(d.etaMinutes)}
                      </span>
                    )}
                  </div>

                  {d.status === 'in_transit' && (
                    <div className="mt-2.5">
                      <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
                        <span>{d.pickupCity}</span>
                        <span className="font-600 text-primary">{d.routeProgress}%</span>
                        <span>{d.destinationCity}</span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full gradient-primary rounded-full transition-all duration-700"
                          style={{ width: `${d.routeProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* ── Right: detail panel ── */}
          {selected && (
            <div className="lg:col-span-2 space-y-4">
              {/* Status header card */}
              <div className="card-elevated p-5">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h2 className="text-base font-800 text-foreground">{selected.route}</h2>
                      <StatusBadge variant={statusConfig[selected.status].variant} label={statusConfig[selected.status].label} size="sm" />
                    </div>
                    <p className="text-xs text-muted-foreground">{selected.parcelCategory} · {selected.parcelDescription}</p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
                    <Icon name="TruckIcon" size={13} className="text-primary" />
                    <span className="font-500">{selected.transportMethod}</span>
                  </div>
                </div>

                {/* Key metrics row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">ETA</p>
                    <p className={`text-sm font-700 mt-0.5 ${selected.etaMinutes !== null ? 'text-primary' : 'text-muted-foreground'}`}>
                      {etaLabel(selected.etaMinutes)}
                    </p>
                    {selected.etaUpdatedAt && (
                      <p className="text-[9px] text-muted-foreground mt-0.5">Updated {fmtTime(selected.etaUpdatedAt)}</p>
                    )}
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Progress</p>
                    <p className="text-sm font-700 text-primary mt-0.5 tabular-nums">{selected.routeProgress}%</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Est. Arrival</p>
                    <p className="text-xs font-600 text-foreground mt-0.5">{fmtTime(selected.estimatedArrival)}</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Current Location</p>
                    <p className="text-xs font-600 text-foreground mt-0.5 truncate">
                      {selected.currentLocation || '—'}
                    </p>
                  </div>
                </div>

                {/* Traveler actions */}
                {isTraveler && ['pickup_confirmed', 'in_transit'].includes(selected.status) && (
                  <div className="mt-4 pt-4 border-t border-border space-y-3">
                    <p className="text-xs font-700 text-foreground">Traveler Actions</p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      {/* ETA update */}
                      <div className="flex gap-2 flex-1">
                        <input
                          type="number"
                          min="0"
                          placeholder="ETA in minutes"
                          value={etaInput}
                          onChange={e => setEtaInput(e.target.value)}
                          className="flex-1 text-xs px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                        <button
                          onClick={handleUpdateEta}
                          disabled={updatingEta || !etaInput}
                          className="px-3 py-2 rounded-lg bg-primary text-white text-xs font-600 disabled:opacity-50 hover:bg-primary/90 transition-colors whitespace-nowrap"
                        >
                          {updatingEta ? 'Saving…' : 'Set ETA'}
                        </button>
                      </div>
                      {/* GPS share */}
                      <button
                        onClick={handleShareGPS}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-primary/30 text-primary text-xs font-600 hover:bg-primary/5 transition-colors"
                      >
                        <Icon name="MapPinIcon" size={13} />
                        Share GPS
                      </button>
                    </div>
                    {/* Location checkpoint */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Location checkpoint (e.g. Mtito Andei)"
                        value={locationInput}
                        onChange={e => setLocationInput(e.target.value)}
                        className="flex-1 text-xs px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                      <button
                        onClick={handleUpdateLocation}
                        disabled={updatingLocation || !locationInput.trim()}
                        className="px-3 py-2 rounded-lg bg-accent text-white text-xs font-600 disabled:opacity-50 hover:bg-accent/90 transition-colors whitespace-nowrap"
                      >
                        {updatingLocation ? 'Saving…' : 'Checkpoint'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Tab switcher */}
              <div className="flex gap-1 bg-muted/50 rounded-xl p-1 w-fit">
                {(['timeline', 'breadcrumbs'] as const).map(t => (
                  <button
                    key={t}
                    onClick={() => setTab(t)}
                    className={`px-4 py-1.5 rounded-lg text-xs font-600 transition-all ${
                      tab === t ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t === 'timeline' ? 'Status Timeline' : 'Breadcrumbs'}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              {tab === 'timeline' ? (
                <StatusTimeline delivery={selected} events={events} />
              ) : (
                <BreadcrumbMap delivery={selected} breadcrumbs={breadcrumbs} />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
